import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../../api/endpoints';
import { fileUrl } from '../../api/client';
import PageHeader from '../../components/PageHeader';
import { Badge, Button, Loading, Row } from '../../components/Ui';
import { Reveal } from '../../components/Motion';
import { useToast } from '../../context/ToastContext';
import { currency, titleCase } from '../../utils/format';

/** One listing. The seller's number stays hidden until an enquiry is sent. */
const ListingDetailPage = ({ kind: fixedKind }) => {
  const { id, kind: routeKind } = useParams();
  const kind = fixedKind || (routeKind === 'animal' ? 'animal' : 'property');
  const isProperty = kind === 'property';
  const toast = useToast();

  const [item, setItem] = useState(null);
  const [species, setSpecies] = useState(null);
  useEffect(() => {
    if (kind !== 'animal') return;
    api.listings.animal.schema().then((r) => setSpecies(r.data)).catch(() => {});
  }, [kind]);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const load = useCallback(() => {
    api.listings[kind].detail(id).then((r) => setItem(r.data)).catch(() => setItem(null));
  }, [kind, id]);
  useEffect(() => { load(); }, [load]);

  if (!item) return <Loading text="Loading…" />;

  const enquire = async () => {
    setSending(true);
    try {
      const res = await api.listings[kind].enquire(id, { message });
      toast.success(`${res.data.contactName || 'Seller'} · ${res.data.contactPhone}`, 'Enquiry sent');
      setMessage(''); load();
    } catch (err) { toast.error(err.message); } finally { setSending(false); }
  };

  const p = item.property || {};
  const a = item.animal || {};
  const spec = species ? (species[a.animalType] || species.other) : null;
  const specs = isProperty
    ? [['Type', titleCase(p.propertyType)], ['Bedrooms', p.bedrooms || null], ['Bathrooms', p.bathrooms || null],
      ['Area', p.areaValue ? `${p.areaValue} ${p.areaUnit}` : null], ['Furnishing', titleCase(p.furnishing)],
      ['Floor', p.floor], ['Facing', p.facing]]
    : [['Animal', titleCase(a.animalType)],
      // Facts are labelled by the species spec, so a bird shows "Hand-tamed"
      // and a cow shows "Calvings" without this page knowing either.
      ...((spec && spec.fields) || []).map((fl) => {
        const v = a[fl.key];
        if (fl.kind === 'bool') return [fl.label, v ? 'Yes' : null];
        if (fl.key === 'ageMonths') return ['Age', v ? `${Math.floor(v / 12)} yr ${v % 12} mo` : null];
        if (fl.kind === 'number') return [fl.label, v ? `${v}${fl.unit ? ` ${fl.unit}` : ''}` : null];
        return [fl.label, v ? titleCase(String(v)) : null];
      })];

  return (
    <>
      <PageHeader title={item.title} subtitle={[item.city, item.district].filter(Boolean).join(', ')} back />

      {item.images && item.images.length ? (
        <div className="row" style={{ gap: 10, overflowX: 'auto', marginBottom: 16 }}>
          {item.images.map((u, i) => (
            <img key={i} src={fileUrl(u)} alt="" style={{ height: 240, borderRadius: 14, objectFit: 'cover', flex: '0 0 auto' }} />
          ))}
        </div>
      ) : null}
      {item.video ? (
        <div style={{ marginBottom: 16 }}>
          <div className="t-small" style={{ fontWeight: 700, marginBottom: 6 }}>🎬 Video</div>
          <video src={fileUrl(item.video)} controls style={{ width: '100%', maxHeight: 420, borderRadius: 14, background: '#000' }} />
        </div>
      ) : null}

      <div className="grid cols-2" style={{ gap: 16, alignItems: 'start' }}>
        <div style={{ display: 'grid', gap: 16 }}>
          <Reveal className="card">
            <div className="row" style={{ alignItems: 'baseline', gap: 10 }}>
              <strong style={{ fontSize: 28, color: 'var(--primary)' }}>{currency(item.price)}</strong>
              {item.priceUnit !== 'total' ? <span className="t-small">{item.priceUnit}</span> : null}
              <Badge value={item.dealType === 'rent' ? 'For rent' : 'For sale'} />
            </div>
            {item.deposit ? <div className="t-small">Security deposit {currency(item.deposit)}</div> : null}
            {item.negotiable ? <div className="t-tiny">Price negotiable</div> : null}
            {item.isClosed ? (
              <div style={{ marginTop: 8, background: 'var(--danger)', color: '#fff', display: 'inline-block', padding: '4px 10px', borderRadius: 6, fontWeight: 800, fontSize: 12 }}>
                {item.dealType === 'rent' ? 'ALREADY RENTED OUT' : 'ALREADY SOLD'}
              </div>
            ) : null}
          </Reveal>

          <Reveal delay={60} className="card">
            <h3 className="t-title" style={{ marginBottom: 10 }}>Details</h3>
            {specs.filter(([, v]) => v).map(([l, v]) => <Row key={l} label={l} value={String(v)} />)}
            {p.amenities && p.amenities.length ? (
              <div className="row" style={{ gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
                {p.amenities.map((am) => <span key={am} className="t-tiny" style={{ background: 'var(--bg)', padding: '5px 10px', borderRadius: 999 }}>{am}</span>)}
              </div>
            ) : null}
          </Reveal>

          {item.description ? (
            <Reveal delay={90} className="card">
              <h3 className="t-title" style={{ marginBottom: 6 }}>About</h3>
              <p style={{ lineHeight: 1.6 }}>{item.description}</p>
            </Reveal>
          ) : null}
        </div>

        <Reveal delay={120} className="card">
          <h3 className="t-title" style={{ marginBottom: 10 }}>Contact the seller</h3>
          {item.contactHidden ? (
            <>
              <p className="t-small" style={{ marginBottom: 12 }}>
                Send an enquiry to see {item.contactName || 'the seller'}&apos;s number. They will be told you asked.
              </p>
              <textarea className="textarea" rows={3} placeholder="Is it still available? When can I visit?"
                value={message} onChange={(e) => setMessage(e.target.value)} />
              <Button title="Send enquiry & see number" icon="📞" loading={sending} onClick={enquire} full style={{ marginTop: 10 }} />
            </>
          ) : (
            <a href={`tel:${item.contactPhone}`} className="btn primary full">
              📞 Call {item.contactName || 'seller'} · {item.contactPhone}
            </a>
          )}
          <div className="t-tiny" style={{ marginTop: 12, textAlign: 'center' }}>
            {item.views || 0} views · {item.enquiries || 0} enquiries
          </div>
        </Reveal>
      </div>
    </>
  );
};

export default ListingDetailPage;
